import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  IconButton,
  Snackbar,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import SiteInformationViewer from "../components/SiteInformationViewer";
import UserDetails from "../components/UserDetails";
import HazardPopup from "../components/HazardPopupResponse";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PrintPDFButton from "../components/PrintPDFButton";
// ViewResponsePage.jsx
import RectangleLinkButton from "../components/RectangleLinkButton";
import "dayjs/locale/en-gb";

// Modules
import AnswerField from "../modules/AnswerField";
import Banner from "../modules/Banner";
import Section from "../modules/Section";
import Header from "../modules/Header";
import Paragraph from "../modules/Paragraph";
import SubHeader from "../modules/SubHeader";
import DateTimeField from "../modules/DateTimeField";
import DateRangeField from "../modules/DateRangeField";
import TimeRangeField from "../modules/TimeRangeField";
import SignatureField from "../modules/SignatureField";
import UserMultiSelectResponse from "../modules/UserMultiSelectResponse";
import CustomDocumentUploaderView from "../modules/CustomViewDocumentUploader";
import InspectionItemField from "../modules/InspectionItemField";
import {
  evaluateCombinedAuditStatus,
  buildAuditBanner,
} from "../helpers/auditScoring";

import {
  uploadPhoto,
  uploadImage,
  uploadMultipleImages,
  handleFileChangeUpload,
} from "../modules/uploadHandlers";
import CheckboxField from "../modules/CheckboxField";
import CheckboxGroupField from "../modules/CheckboxGroupField";
import RadioButtonGroupField from "../modules/RadioButtonFieldGroup";
import DoubleShortAnswerField from "../modules/DoubleShortAnswerField";
import TableField from "../modules/TableField";
import DynamicQuestionGroupField from "../modules/DynamicQuestionGroupField";
import EmailField from "../modules/EmailField";
import DropdownSelectField from "../modules/DropdownSelectField";
import AuditCheckboxGroupField from "../modules/AuditCheckboxGroupField";
// Custom Hooks
import useDocumentCheckerStatus from "../Hooks/useDocumentCheckerStatus";
import useFormData from "../Hooks/useFormData";
import ZoneHazardPopup from "../components/ZoneHazardPopup";
import ServiceProviderSelectResponse from "../modules/ServiceProviderSelectResponse";
import RoofPlanViewer from "../components/RoofPlanViewer";

// --- Audit helpers (module-level pure functions only — no hooks here) ---
const CRIT_LEVELS = ["High", "Medium", "Low"];

const normalizeAuditCrit = (ac) => ({
  High: Array.isArray(ac?.High) ? ac.High : [],
  Medium: Array.isArray(ac?.Medium) ? ac.Medium : [],
  Low: Array.isArray(ac?.Low) ? ac.Low : [],
});

const countCritQuestionHits = (modules, responses = {}) => {
  const counts = { High: 0, Medium: 0, Low: 0 };
  const walk = (mod) => {
    if (mod.type === "audit_checkbox_group") {
      const ac = normalizeAuditCrit(mod.auditCriticality);
      const raw = responses?.[mod.id];
      const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];
      CRIT_LEVELS.forEach((lvl) => {
        if (ac[lvl].length && ac[lvl].some((opt) => selected.includes(opt))) {
          counts[lvl] += 1;
        }
      });
    }
    if (mod.type === "inspection_item") {
      const ac = normalizeAuditCrit(mod.auditCriticality);
      const raw = responses?.[mod.id];
      const sel = raw?.selection ?? (typeof raw === "string" ? raw : null);
      const selected = sel ? [sel] : [];
      CRIT_LEVELS.forEach((lvl) => {
        if (ac[lvl].length && ac[lvl].some((opt) => selected.includes(opt))) {
          counts[lvl] += 1;
        }
      });
    }
    if (Array.isArray(mod.children)) mod.children.forEach(walk);
    if (mod.type === "dynamic_question_group" && Array.isArray(mod.questions)) {
      mod.questions.forEach((q) => {
        if (q.type === "audit_checkbox_group") {
          const ac = normalizeAuditCrit(q.auditCriticality);
          const raw = responses?.[q.id];
          const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];
          CRIT_LEVELS.forEach((lvl) => {
            if (
              ac[lvl].length &&
              ac[lvl].some((opt) => selected.includes(opt))
            ) {
              counts[lvl] += 1;
            }
          });
        }
      });
    }
  };
  modules.forEach(walk);
  return counts;
};

// map final/locked statuses to label + color
const auditStatusMeta = (status) => {
  const s = Number(status);
  if (s === 15) return { text: "FAIL", color: "#d00000" };
  if (s === 14) return { text: "NEEDS IMPROVEMENT", color: "#ff9a02" };
  if (s === 13) return { text: "PASS", color: "#00cfaa" };
  if (s === 8) return { text: "COMPLETED (No Thresholds)", color: "#666" };
  return { text: `STATUS ${s}`, color: "#666" };
};

const meetsBucket = (counts, bucket) => {
  if (!bucket) return false;
  const needH = Number(bucket.High) || 0;
  const needM = Number(bucket.Medium) || 0;
  const needL = Number(bucket.Low) || 0;
  return (
    (needH > 0 && counts.High >= needH) ||
    (needM > 0 && counts.Medium >= needM) ||
    (needL > 0 && counts.Low >= needL)
  );
};

// NOTE: isAuditForm / auditEval / auditBanner / thresholdEnabled / countsText
// were previously placed here (outside the component) — that was the bug.
// They are now declared correctly as useMemo hooks inside ViewResponsePage below.

function isModuleVisible(module, responseData, responseStatus) {
  // 1) respect visibleWhenStatus
  if (!matchesStatus(module, responseStatus)) {
    return false;
  }
  if (
    !module.visibilityCondition ||
    typeof module.visibilityCondition !== "object"
  ) {
    return true;
  }
  const { moduleId, operator, value } = module.visibilityCondition;
  const targetResponse = responseData[moduleId];
  switch (operator) {
    case "equals":
      return Array.isArray(targetResponse)
        ? targetResponse.includes(value)
        : targetResponse === value;
    case "not_equals":
      return Array.isArray(targetResponse)
        ? !targetResponse.includes(value)
        : targetResponse !== value;
    case "contains":
      return Array.isArray(targetResponse) && targetResponse.includes(value);
    default:
      return true;
  }
}

const API_BASE_URL = "https://formbuilderapi.getforsite.com";

// === Visible-status helper ===
const matchesStatus = (module, responseStatus) => {
  const status = Number(responseStatus);

  // never show approvalRequired fields at status 1
  if (module?.approvalRequired === true && status === 1) return false;

  const v = module.visibleWhenStatus;
  if (v == null) return true; // no rule => show
  const arr = Array.isArray(v) ? v : [v]; // normalize
  if (arr.includes(0)) return true; // 0 => always show
  return arr.includes(status); // otherwise must match
};

const isRenderable = (mod, responseData, responseStatus) => {
  // must pass status gates AND any visibilityCondition
  return (
    matchesStatus(mod, responseStatus) &&
    isModuleVisible(mod, responseData, responseStatus)
  );
};

// --- Video helpers ---
const getYouTubeId = (urlStr) => {
  try {
    const u = new URL(urlStr);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.split("/")[1] || null;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) {
        return u.pathname.split("/")[2] || null;
      }
      return u.searchParams.get("v");
    }
  } catch {}
  return null;
};

const getVimeoId = (urlStr) => {
  try {
    const u = new URL(urlStr);
    if (u.hostname.includes("vimeo.com")) {
      const parts = u.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      return /^\d+$/.test(last) ? last : null;
    }
  } catch {}
  return null;
};

const isDirectVideo = (urlStr) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(urlStr);

const buildEmbed = (
  urlStr,
  { autoplay = false, muted = false, loop = false } = {},
) => {
  const yt = getYouTubeId(urlStr);
  if (yt) {
    const params = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
      autoplay: autoplay ? "1" : "0",
      mute: muted ? "1" : "0",
      loop: loop ? "1" : "0",
      ...(loop ? { playlist: yt } : {}),
    });
    return {
      kind: "iframe",
      src: `https://www.youtube.com/embed/${yt}?${params.toString()}`,
    };
  }

  const vimeo = getVimeoId(urlStr);
  if (vimeo) {
    const params = new URLSearchParams({
      autopause: "1",
      playsinline: "1",
      autoplay: autoplay ? "1" : "0",
      muted: muted ? "1" : "0",
      loop: loop ? "1" : "0",
      title: "0",
      byline: "0",
      portrait: "0",
    });
    return {
      kind: "iframe",
      src: `https://player.vimeo.com/video/${vimeo}?${params.toString()}`,
    };
  }

  if (isDirectVideo(urlStr)) {
    return { kind: "video", src: urlStr };
  }

  return { kind: "link", href: urlStr };
};

// Responsive 16/9 wrapper utility
const ResponsiveFrame = ({ aspect = 16 / 9, children }) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      pt: `${(1 / aspect) * 100}%`,
      borderRadius: 1,
      overflow: "hidden",
      bgcolor: "black",
    }}
  >
    <Box sx={{ position: "absolute", inset: 0 }}>{children}</Box>
  </Box>
);

// ---------- Validation & correctness helpers (match FormRenderer) ----------
const toArray = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);
const isBlankStr = (v) => (typeof v === "string" ? v.trim() === "" : v == null);

const normArray = (val) => {
  if (Array.isArray(val)) return val.map((v) => String(v).trim());
  if (val == null) return [];
  return [String(val).trim()];
};
const setsEqual = (a, b) => {
  const A = new Set(a),
    B = new Set(b);
  if (A.size !== B.size) return false;
  for (const x of A) if (!B.has(x)) return false;
  return true;
};
const isExactlyCorrect = (userAnswer, correctAnswers) =>
  setsEqual(normArray(userAnswer), normArray(correctAnswers || []));

const readAnswer = (responses, id, parentId) => {
  const flat = responses?.[id];
  if (flat !== undefined) return flat;
  if (parentId && responses?.[parentId]) return responses[parentId][id];
  return undefined;
};

const isAnsweredByType = (type, value, signatures) => {
  switch (type) {
    case "short_answer":
    case "long_answer":
    case "email":
    case "datetime":
      return !isBlankStr(value);

    case "checkbox":
      return value === true || value === false;

    case "checkbox_group":
    case "custom_checkbox_group":
    case "audit_checkbox_group":
      return Array.isArray(value) ? value.length > 0 : !isBlankStr(value);

    case "inspection_item":
      return !!(value?.selection);

    case "email_dropdown":
    case "dropdown_select":
      return Array.isArray(value) ? value.length > 0 : !isBlankStr(value);

    case "radio_button_group":
      return !isBlankStr(value);

    case "time_range":
      return value && value.startTime && value.endTime;

    case "date_range":
      return value && value.startDate && value.endDate;

    case "table":
      return (
        Array.isArray(value) &&
        value.some(
          (row) => row && Object.values(row).some((cell) => !isBlankStr(cell)),
        )
      );

    case "image_upload":
      return Array.isArray(value) ? value.length > 0 : false;

    case "signature":
      return Boolean(
        (Array.isArray(signatures) && signatures.length > 0) ||
          (typeof value === "string" && value.length > 0),
      );

    case "user_details":
      return (
        value &&
        (value.email ||
          value.firstName ||
          value.lastName ||
          value.company ||
          value.phoneNumber)
      );

    case "work_orders":
      return Array.isArray(value) && value.length > 0;

    case "custom_document_uploader":
      return !!value;

    default:
      return true;
  }
};

const resolveRenderedQuestions = (mod, responses) => {
  if (!Array.isArray(mod?.questions)) return [];
  const qs = mod.questions;

  const persisted = responses?.[mod.id]?.__shownIds;
  if (Array.isArray(persisted) && persisted.length) {
    const idSet = new Set(persisted);
    return qs.filter((q) => idSet.has(q.id));
  }
  if (Array.isArray(mod?.questionsShown) && mod.questionsShown.length > 0) {
    const shown = mod.questionsShown;
    if (typeof shown[0] === "object") return shown;
    const idSet = new Set(shown);
    return qs.filter((q) => idSet.has(q.id));
  }
  if (mod.randomise > 0) return qs.slice(0, mod.randomise);
  return qs;
};

const collectRequiredErrors = (
  modules,
  responses,
  signatures,
  responseStatus,
) => {
  const errors = [];

  const walk = (mod, parentId = null) => {
    if (Array.isArray(mod?.children))
      mod.children.forEach((c) => walk(c, mod.id || parentId));

    if (
      mod?.type === "dynamic_question_group" &&
      Array.isArray(mod.questions)
    ) {
      if (!isRenderable(mod, responses, responseStatus)) return;

      const renderedQs = resolveRenderedQuestions(mod, responses);
      renderedQs.forEach((q) => {
        if (!isRenderable(q, responses, responseStatus)) return;
        if (!q?.required) return;

        const vc = q.visibilityCondition;
        if (vc) {
          const target = responses?.[vc.moduleId];
          const visible =
            vc.operator === "equals"
              ? Array.isArray(target)
                ? target.includes(vc.value)
                : target === vc.value
              : vc.operator === "not_equals"
                ? Array.isArray(target)
                  ? !target.includes(vc.value)
                  : target !== vc.value
                : vc.operator === "contains"
                  ? Array.isArray(target) && target.includes(vc.value)
                  : true;
          if (!visible) return;
        }

        const ans = readAnswer(responses, q.id, mod.id);
        if (!isAnsweredByType(q.type, ans, signatures)) errors.push(q.id);
      });
    }

    if (!isRenderable(mod, responses, responseStatus)) return;

    let ans = readAnswer(responses, mod.id, parentId);
    if (
      mod.type === "user_details" &&
      ans === undefined &&
      responses?.user_details
    ) {
      ans = responses.user_details;
    }

    if (mod?.required && !isAnsweredByType(mod.type, ans, signatures)) {
      errors.push(mod.id);
    }
  };

  (modules || []).forEach((m) => walk(m, null));
  return errors;
};

const collectIncorrectAnswerModules = (modules, responses, responseStatus) => {
  const incorrect = [];
  const pushIncorrect = (id, label, picked) =>
    incorrect.push({ id, label, picked: normArray(picked) });

  (modules || []).forEach((mod) => {
    if (!isRenderable(mod, responses, responseStatus)) {
      if (Array.isArray(mod.children)) {
        incorrect.push(
          ...collectIncorrectAnswerModules(
            mod.children,
            responses,
            responseStatus,
          ),
        );
      }
      return;
    }

    if (
      (mod.type === "checkbox_group" || mod.type === "custom_checkbox_group") &&
      Array.isArray(mod.correctResponses) &&
      mod.correctResponses.length > 0
    ) {
      const userAns = responses?.[mod.id];
      if (!isExactlyCorrect(userAns, mod.correctResponses)) {
        if (!(mod.customLogic?.action === "setStatusTo3")) {
          pushIncorrect(mod.id, mod.label, userAns);
        }
      }
    }

    if (mod.type === "dynamic_question_group" && Array.isArray(mod.questions)) {
      const renderedQs = resolveRenderedQuestions(mod, responses);
      renderedQs.forEach((q) => {
        if (!isRenderable(q, responses, responseStatus)) return;

        if (
          Array.isArray(q.correctResponses) &&
          q.correctResponses.length > 0
        ) {
          const raw =
            responses?.[q.id] ??
            (responses?.[mod.id] && responses[mod.id][q.id]);
          if (!isExactlyCorrect(raw, q.correctResponses)) {
            if (!(q.customLogic?.action === "setStatusTo3")) {
              pushIncorrect(q.id, q.label, raw);
            }
          }
        }
      });
    }

    if (Array.isArray(mod.children)) {
      incorrect.push(
        ...collectIncorrectAnswerModules(
          mod.children,
          responses,
          responseStatus,
        ),
      );
    }
  });

  return incorrect;
};

const onlyApprovalRequired = (modules) =>
  (modules || []).filter((m) => m.approvalRequired === true);

function ViewResponsePage() {
  const { formId, responseId } = useParams();
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState("");

  const {
    formStructure,
    responseData,
    setResponseData,
    responseStatus,
    setResponseStatus,
    siteId,
    responseUserId,
    signatures,
    setSignatures,
    requiredDocuments,
    responseRequestedTo,
    responseRequestedToName,
    responseRequestedBy,
  } = useFormData(formId, responseId);

  const [isEditing, setIsEditing] = useState(false);

  const isAudit = formStructure?.formType === "Audit";

  useEffect(() => {
    if (responseStatus !== undefined && responseStatus !== null) {
      // Lock Audit for 8,13,14,15
      if (isAudit && [8, 13, 14, 15].includes(Number(responseStatus))) {
        setIsEditing(false);
        return;
      }
      if ([1, 3, 7].includes(Number(responseStatus))) {
        setIsEditing(true);
      }
    }
  }, [responseStatus, isAudit]);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [userId, setUserId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [isHazardPopupOpen, setHazardPopupOpen] = useState(false);
  const [isZoneHazardPopupOpen, setZoneHazardPopupOpen] = useState(false);
  const [hazards, setHazards] = useState([]);
  const [editedHazards, setEditedHazards] = useState([]);
  const [zoneName, setZoneName] = useState("Roof");

  const printableRef = useRef();

  const localStyles = formStructure?.styles || {};

  useEffect(() => {
    if (responseUserId && !userId) {
      setUserId(responseUserId);
    }
  }, [responseUserId]);

  // All hooks must be above any early returns

  const isLockedStatus = [8, 13, 14, 15].includes(Number(responseStatus));
  const thresholdEnabled = !!formStructure?.threshold?.enabled;

  const isSaveButtonVisible = useMemo(() => {
    if (!formStructure) return false;
    return (
      isEditing ||
      formStructure.modules.some((module) => {
        const raw = module.editableWhenStatus;
        if (raw != null) {
          const allowed = Array.isArray(raw) ? raw.map(Number) : [Number(raw)];
          return allowed.includes(Number(responseStatus));
        }
        return [1, 2, 3, 6, 7, 11].includes(Number(responseStatus));
      })
    );
  }, [formStructure, responseStatus, isEditing]);

  const lockedCounts = useMemo(() => {
    if (!isAudit || !isLockedStatus) return { High: 0, Medium: 0, Low: 0 };
    return countCritQuestionHits(
      formStructure?.modules || [],
      responseData || {},
    );
  }, [isAudit, isLockedStatus, formStructure, responseData]);

  // ── Unified audit evaluation ─────────────────────────────────────────────
  // Replaces the old critCounts / predictedAuditStatus / auditBanner useMemos.
  // These MUST be inside the component — they were previously placed outside
  // at module scope (the bug), which caused an "Invalid hook call" crash.

  const auditEval = useMemo(
    () =>
      isAudit && formStructure
        ? evaluateCombinedAuditStatus(formStructure, responseData || {})
        : null,
    [isAudit, formStructure, responseData],
  );

  // Banner object for the live footer while editing/in-progress
  const auditBanner = useMemo(
    () => (auditEval ? buildAuditBanner(auditEval) : null),
    [auditEval],
  );

  // Counts text shown in the banner detail line (threshold mechanism)
  const countsText = useMemo(() => {
    if (!thresholdEnabled || !formStructure) return "";
    const counts = countCritQuestionHits(
      formStructure.modules || [],
      responseData || {},
    );
    return `${counts.High} High, ${counts.Medium} Medium, ${counts.Low} Low`;
  }, [thresholdEnabled, formStructure, responseData]);

  const handleFieldChange = useCallback(
    (key, value) => {
      const keys = key.split(".");
      if (keys.length === 2) {
        const [parentKey, childKey] = keys;
        setResponseData((prevData) => ({
          ...prevData,
          [parentKey]: {
            ...prevData[parentKey],
            [childKey]: value,
          },
        }));
      } else {
        setResponseData((prevData) => ({
          ...prevData,
          [key]: value,
        }));
      }
    },
    [setResponseData],
  );

  const handleSignatureChange = (moduleId, signatureData) => {
    setSignatures((prevSignatures) => {
      const existingIndex = prevSignatures.findIndex(
        (sig) => sig.module_id === moduleId,
      );
      if (existingIndex !== -1) {
        const updatedSignatures = [...prevSignatures];
        updatedSignatures[existingIndex].signature_data = signatureData;
        return updatedSignatures;
      } else {
        return [
          ...prevSignatures,
          { module_id: moduleId, signature_data: signatureData },
        ];
      }
    });
  };

  const handleRemoveSignature = (moduleId) => {
    setSignatures((prevSignatures) =>
      prevSignatures.filter((sig) => sig.module_id !== moduleId),
    );
  };

  const confirmEmail = async () => {
    const email = responseData.email;
    if (!email || email.trim() === "") {
      setEmailError("Please enter an email address.");
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/confirm-email`, {
        email,
      });
      const { userId: fetchedUserId, companyId: fetchedCompanyId } = res.data;
      setUserId(fetchedUserId);
      setCompanyId(fetchedCompanyId);
      setEmailConfirmed(true);
      setEmailError("");
      alert("Email confirmed successfully.");
    } catch (err) {
      console.error("Error confirming email:", err);
      setEmailError(
        err.response?.data?.error ||
          "An error occurred while confirming the email.",
      );
      setUserId(null);
      setCompanyId(null);
      setEmailConfirmed(false);
    }
  };

  const handleTableCellChange = (key, rowIndex, column, value) => {
    setResponseData((prevResponses) => {
      const currentRows = prevResponses[key] || [];
      const updatedRows = currentRows.map((row, idx) =>
        idx === rowIndex ? { ...row, [column]: value } : row,
      );
      return { ...prevResponses, [key]: updatedRows };
    });
  };

  const handleAddTableRow = (key, columns) => {
    setResponseData((prevResponses) => {
      const currentRows = prevResponses[key] || [];
      const newRow = columns.reduce((acc, col) => ({ ...acc, [col]: "" }), {});
      return { ...prevResponses, [key]: [...currentRows, newRow] };
    });
  };

  const handleRemoveTableRow = (key, rowIndex) => {
    setResponseData((prevResponses) => {
      const currentRows = prevResponses[key] || [];
      const newRows = currentRows.filter((_, idx) => idx !== rowIndex);
      return { ...prevResponses, [key]: newRows };
    });
  };

  const handleDateRangeChange = (key, field, value) => {
    setResponseData((prevResponses) => ({
      ...prevResponses,
      [key]: {
        ...prevResponses[key],
        [field]: value,
      },
    }));
  };

  const handleTimeRangeChange = (key, field, value) => {
    setResponseData((prevResponses) => ({
      ...prevResponses,
      [key]: {
        ...prevResponses[key],
        [field]: value,
      },
    }));
  };

  const handleRemoveImageFromResponse = (key, imageIndex) => {
    setResponseData((prevResponses) => {
      const updatedImages = prevResponses[key].filter(
        (_, idx) => idx !== imageIndex,
      );
      return { ...prevResponses, [key]: updatedImages };
    });
  };

  const handleRemovePhoto = (key, photoIndex) => {
    setResponseData((prevResponses) => {
      const updatedPhotos = prevResponses[key].filter(
        (_, idx) => idx !== photoIndex,
      );
      return { ...prevResponses, [key]: updatedPhotos };
    });
  };

  const fetchHazards = async () => {
    if (editedHazards.length > 0) {
      setHazards(responseData.hazards || []);
      setEditedHazards(responseData.hazards || []);
    } else {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/hazards/getHazardRegister`,
          {
            params: { siteId },
          },
        );
        setHazards(res.data);
        setEditedHazards(res.data);
      } catch (err) {
        console.error("Error fetching hazard register:", err);
      }
    }
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);

      // Validate only approvalRequired: true modules
      const arModules = onlyApprovalRequired(formStructure.modules || []);

      const requiredErrors = collectRequiredErrors(
        arModules,
        responseData,
        signatures,
        responseStatus,
      );
      if (requiredErrors.length > 0) {
        alert(
          "Please complete all required* fields before saving. (fields with *)",
        );
        setLoading(false);
        return;
      }

      const incorrect = collectIncorrectAnswerModules(
        arModules,
        responseData,
        responseStatus,
      );
      if (incorrect.length > 0) {
        const msg = incorrect
          .map(
            (m) =>
              `• ${m.label || m.id} — your selection: ${m.picked.join(", ") || "none"}`,
          )
          .join("\n");
        alert(`Some approval fields have incorrect answers:\n\n${msg}`);
        setLoading(false);
        return;
      }

      await axios.put(
        `${API_BASE_URL}/api/forms/${formId}/responses/${responseId}`,
        { data: responseData, signatures },
      );

      setLoading(false);
      setSuccess(true);
      alert("Changes saved successfully.");
    } catch (err) {
      console.error("Error saving changes:", err);
      setLoading(false);
      alert("An error occurred while saving changes.");
    }
  };

  const handleLocalSave = (newHazards) => {
    setEditedHazards(newHazards);
    localStorage.setItem(`editedHazards_${formId}`, JSON.stringify(newHazards));
    setSuccess(true);
    alert("Changes saved locally.");
  };

  const handleApplyChanges = async (updatedHazards) => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}/api/hazards/updateHazards`,
        {
          hazards: updatedHazards,
        },
      );
      if (res.data.success) {
        setApplySuccess(true);
        setEditedHazards([]);
        localStorage.removeItem(`editedHazards_${formId}`);
        fetchHazards();
        setHazardPopupOpen(false);
      } else {
        setApplyError("Failed to apply changes. Please try again.");
      }
    } catch (err) {
      console.error("Error applying changes:", err);
      setApplyError(
        err.response?.data?.error || "An unexpected error occurred.",
      );
    } finally {
      setLoading(false);
    }
  };

  const documentStatuses = useDocumentCheckerStatus(
    formStructure,
    responseUserId,
  );

  const handleSubmitAsApproved = async () => {
    try {
      setLoading(true);
      await axios.put(
        `${API_BASE_URL}/api/forms/${formId}/responses/${responseId}`,
        {
          data: responseData,
          signatures: signatures,
          status: 0,
        },
      );
      setResponseStatus(0);
      setIsEditing(false);
      setLoading(false);
      setSuccess(true);
      alert("Response submitted as approved.");
    } catch (err) {
      console.error("Error submitting as approved:", err);
      setLoading(false);
      alert("An error occurred while submitting as approved.");
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const redirectToBlob = () => {
      const successMessageHTML = `
        <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; background-color: #F0F0F0; text-align: center; padding: 50px; }
              .message { background-color: #FFD902; border: 1px solid #ddd; padding: 20px; display: inline-block; margin: 0 auto; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
            </style>
          </head>
          <body>
            <div class="message"><h1>The form was submitted successfully.</h1></div>
          </body>
        </html>`;
      const blob = new Blob([successMessageHTML], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      setTimeout(() => {
        window.location.href = url;
        URL.revokeObjectURL(url);
      }, 1000);
    };

    try {
      // Required field validation
      const requiredErrors = collectRequiredErrors(
        formStructure.modules || [],
        responseData,
        signatures,
        responseStatus,
      );
      if (requiredErrors.length > 0) {
        alert("Please answer all required questions before submitting.");
        setLoading(false);
        return;
      }

      // Correct-answer validation
      const incorrect = collectIncorrectAnswerModules(
        formStructure.modules || [],
        responseData,
        responseStatus,
      );
      if (incorrect.length > 0) {
        const msg = incorrect
          .map(
            (m) =>
              `• ${m.label || m.id} — your selection: ${m.picked.join(", ") || "none"}`,
          )
          .join("\n");
        alert(`Some answers are incorrect:\n\n${msg}`);
        setLoading(false);
        return;
      }

      // Optional: upload any new required-doc Files present in responseData
      let uploadedDocumentUrls = [];
      if (requiredDocuments && requiredDocuments.length > 0) {
        const formData = new FormData();
        for (const doc of requiredDocuments) {
          if (responseData[doc.id] instanceof File) {
            formData.append(
              `documents[${doc.id}]`,
              responseData[doc.id],
              responseData[doc.id].name,
            );
          }
        }
        if (Array.from(formData.keys()).length > 0) {
          const uploadRes = await axios.post(
            `${API_BASE_URL}/api/forms/${formId}/upload-documents`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          uploadedDocumentUrls = uploadRes.data.uploadedUrls;
        }
      }

      // ── Decide final status using the unified evaluator ──────────────────
      // Replaces the old evaluateAuditThresholdStatus call. The unified
      // evaluator handles mandatory-fail + threshold + scoring in one pass.
      let finalStatus;
      let finalResponseData = { ...responseData };

      if (isAudit) {
        const evalResult = evaluateCombinedAuditStatus(
          formStructure,
          responseData,
        );
        // evaluateCombinedAuditStatus returns 15 / 14 / 13 / 8 directly
        finalStatus = evalResult.status ?? 8;

        // Persist the score percentage alongside the response data
        if (evalResult.scoringEnabled && evalResult.percent !== null) {
          finalResponseData._auditScore = evalResult.percent;
        }
      } else {
        // Non-Audit behaviour (unchanged)
        finalStatus = formStructure?.approvalRequired ? 3 : 0;
      }

      const payload = {
        data: finalResponseData,
        userId,
        companyId,
        documents: uploadedDocumentUrls,
        signatures,
        status: finalStatus,
      };

      let submissionRes;
      if (responseId) {
        submissionRes = await axios.put(
          `${API_BASE_URL}/api/forms/${formId}/responses/${responseId}`,
          payload,
        );
        console.log(
          `Response updated successfully! Response ID: ${responseId}`,
        );
      } else {
        submissionRes = await axios.post(
          `${API_BASE_URL}/api/forms/${formId}/responses`,
          payload,
        );
        console.log(
          `Form submitted successfully! Response ID: ${submissionRes.data.responseId}`,
        );
      }

      // lock UI immediately if Audit ended in a locked status
      setResponseStatus(finalStatus);
      setIsEditing(!(isAudit && [8, 13, 14, 15].includes(finalStatus)));

      const { uploadedUrl: newUploadedUrl } = submissionRes.data || {};
      setUploadedUrl(newUploadedUrl || "");
      setSuccess(true);
      redirectToBlob();
    } catch (err) {
      console.error("Error submitting form:", err);
      alert(
        err.response?.data?.error
          ? `Submission failed: ${err.response.data.error}`
          : "An unexpected error occurred while submitting the form.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!formStructure || responseData === null) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Score shown on the PDF cover — prefer the score frozen at submission,
  // fall back to the live calculation (mirrors the on-screen result chip).
  const pdfAuditScore =
    responseData?._auditScore ??
    (formStructure?.scoring?.enabled && auditEval ? auditEval.percent : undefined);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Container
        maxWidth="sm"
        sx={{
          width: "100%",
          maxWidth: 600,
          px: 2,
          paddingBottom: "220px",
          boxSizing: "border-box",
        }}
      >
        <Box display="flex" justifyContent="flex-end" marginY={2}>
          <PrintPDFButton
            formStructure={formStructure}
            responseData={responseData}
            signatures={signatures}
            companyName={responseRequestedToName}
            responseStatus={responseStatus}
            auditScore={pdfAuditScore}
          />
        </Box>

        {/* ── Locked audit result chip (submitted/completed audits only) ── */}
        {isAudit && [8, 13, 14, 15].includes(Number(responseStatus)) && (() => {
          const meta = auditStatusMeta(responseStatus);

          // Prefer the score frozen at submission time; fall back to live calc
          const storedScore = responseData?._auditScore;
          const scoringEnabled = Boolean(formStructure?.scoring?.enabled);
          const liveScore = scoringEnabled && auditEval ? auditEval.percent : null;
          const displayScore = storedScore ?? liveScore;

          return (
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.25,
                bgcolor: meta.color,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                mb: 2,
                minWidth: 120,
                textAlign: "center",
              }}
            >
              {displayScore !== null && displayScore !== undefined && (
                <Typography sx={{ fontSize: "1.8rem", fontWeight: 700, lineHeight: 1 }}>
                  {displayScore}%
                </Typography>
              )}
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, letterSpacing: 0.5 }}>
                {meta.text}
              </Typography>
            </Box>
          );
        })()}

        <div
          ref={printableRef}
          id="printable-content"
          style={{
            width: "100%",
            maxWidth: "600px",
            margin: "0 auto",
            boxSizing: "border-box",
          }}
        >
          {formStructure.modules
            .filter((m) => matchesStatus(m, responseStatus))
            .map((module, index) => {
              const key =
                module.id || module.label || module.text || `module-${index}`;
              const answer = responseData[key] || "";
              const status = Number(responseStatus);
              const isFieldEditable = (() => {
                const status = Number(responseStatus);

                // Hard lock for Audit in statuses 8,13,14,15
                if (isAudit && isLockedStatus) return false;

                if (
                  (status === 7 || status === 2) &&
                  !module.approvalRequired
                ) {
                  return true;
                }
                if (module.approvalRequired) {
                  return [3, 8].includes(status);
                }
                if (module.editableWhenStatus != null) {
                  const allowed = Array.isArray(module.editableWhenStatus)
                    ? module.editableWhenStatus.map(Number)
                    : [Number(module.editableWhenStatus)];
                  return allowed.includes(status);
                }
                return isEditing && [1, 2, 3, 6, 7, 11].includes(status);
              })();

              if (!isModuleVisible(module, responseData, responseStatus)) {
                return null;
              }

              switch (module.type) {
                case "banner":
                  return (
                    <Banner
                      key={key}
                      module={module}
                      styles={localStyles}
                      fieldKey={key}
                      isEditable={isFieldEditable}
                    />
                  );
                case "section": {
                  const visibleKids =
                    module.children?.filter(
                      (child) =>
                        matchesStatus(child, responseStatus) &&
                        isModuleVisible(child, responseData, responseStatus),
                    ) || [];
                  const filteredModule = { ...module, children: visibleKids };
                  return (
                    <Section
                      key={key}
                      module={filteredModule}
                      formId={formId}
                      styles={localStyles}
                      fieldKey={key}
                      isEditable={isFieldEditable}
                      responseStatus={responseStatus}
                      userId={userId}
                      setUserId={setUserId}
                      setCompanyId={setCompanyId}
                      responseData={responseData}
                      handleFieldChange={handleFieldChange}
                      setResponseData={setResponseData}
                      signatures={signatures}
                      handleSignatureChange={handleSignatureChange}
                      handleRemoveSignature={handleRemoveSignature}
                      documentStatuses={documentStatuses}
                      responseId={responseId}
                      siteId={siteId}
                      companyId={companyId}
                      responseRequestedTo={responseRequestedTo}
                      openHazardPopup={() => setHazardPopupOpen(true)}
                      openZoneHazardPopup={(zn) => {
                        setZoneName(zn || "Roof");
                        setZoneHazardPopupOpen(true);
                      }}
                      onUploadPhoto={(fieldKey, file) =>
                        uploadPhoto({
                          docId: fieldKey,
                          file,
                          formId,
                          setLoading,
                          setResponseData,
                          setSuccess,
                        })
                      }
                      onUploadImage={(fieldKey, files) =>
                        uploadMultipleImages({
                          docId: fieldKey,
                          files,
                          formId,
                          setLoading,
                          setResponseData,
                          setSuccess,
                        })
                      }
                      handleTableCellChange={handleTableCellChange}
                      handleAddTableRow={handleAddTableRow}
                      handleRemoveTableRow={handleRemoveTableRow}
                      handleDateRangeChange={handleDateRangeChange}
                      handleTimeRangeChange={handleTimeRangeChange}
                      handleRemoveImageFromResponse={handleRemoveImageFromResponse}
                      handleRemovePhoto={handleRemovePhoto}
                      setLoading={setLoading}
                      setSuccess={setSuccess}
                    />
                  );
                }

                case "contractorClosureSection": {
                  const visibleCCKids =
                    module.children?.filter(
                      (child) =>
                        matchesStatus(child, responseStatus) &&
                        isModuleVisible(child, responseData, responseStatus),
                    ) || [];
                  const filteredCCModule = {
                    ...module,
                    children: visibleCCKids,
                  };
                  return (
                    <Section
                      key={key}
                      module={filteredCCModule}
                      formId={formId}
                      styles={localStyles}
                      fieldKey={key}
                      isEditable={isFieldEditable}
                      responseStatus={responseStatus}
                      userId={userId}
                      setUserId={setUserId}
                      setCompanyId={setCompanyId}
                      responseData={responseData}
                      handleFieldChange={handleFieldChange}
                      setResponseData={setResponseData}
                      signatures={signatures}
                      handleSignatureChange={handleSignatureChange}
                      handleRemoveSignature={handleRemoveSignature}
                      documentStatuses={documentStatuses}
                      responseId={responseId}
                      siteId={siteId}
                      companyId={companyId}
                      responseRequestedTo={responseRequestedTo}
                      openHazardPopup={() => setHazardPopupOpen(true)}
                      openZoneHazardPopup={(zn) => {
                        setZoneName(zn || "Roof");
                        setZoneHazardPopupOpen(true);
                      }}
                      onUploadPhoto={(fieldKey, file) =>
                        uploadPhoto({
                          docId: fieldKey,
                          file,
                          formId,
                          setLoading,
                          setResponseData,
                          setSuccess,
                        })
                      }
                      onUploadImage={(fieldKey, files) =>
                        uploadMultipleImages({
                          docId: fieldKey,
                          files,
                          formId,
                          setLoading,
                          setResponseData,
                          setSuccess,
                        })
                      }
                      handleTableCellChange={handleTableCellChange}
                      handleAddTableRow={handleAddTableRow}
                      handleRemoveTableRow={handleRemoveTableRow}
                      handleDateRangeChange={handleDateRangeChange}
                      handleTimeRangeChange={handleTimeRangeChange}
                      handleRemoveImageFromResponse={handleRemoveImageFromResponse}
                      handleRemovePhoto={handleRemovePhoto}
                      setLoading={setLoading}
                      setSuccess={setSuccess}
                    />
                  );
                }

                case "adminClosureSection": {
                  const visibleACKids =
                    module.children?.filter(
                      (child) =>
                        matchesStatus(child, responseStatus) &&
                        isModuleVisible(child, responseData, responseStatus),
                    ) || [];
                  const filteredACModule = {
                    ...module,
                    children: visibleACKids,
                  };
                  return (
                    <Section
                      key={key}
                      module={filteredACModule}
                      formId={formId}
                      styles={localStyles}
                      fieldKey={key}
                      isEditable={isFieldEditable}
                      responseStatus={responseStatus}
                      userId={userId}
                      setUserId={setUserId}
                      setCompanyId={setCompanyId}
                      responseData={responseData}
                      handleFieldChange={handleFieldChange}
                      setResponseData={setResponseData}
                      signatures={signatures}
                      handleSignatureChange={handleSignatureChange}
                      handleRemoveSignature={handleRemoveSignature}
                      documentStatuses={documentStatuses}
                      responseId={responseId}
                      siteId={siteId}
                      companyId={companyId}
                      responseRequestedTo={responseRequestedTo}
                      openHazardPopup={() => setHazardPopupOpen(true)}
                      openZoneHazardPopup={(zn) => {
                        setZoneName(zn || "Roof");
                        setZoneHazardPopupOpen(true);
                      }}
                      onUploadPhoto={(fieldKey, file) =>
                        uploadPhoto({
                          docId: fieldKey,
                          file,
                          formId,
                          setLoading,
                          setResponseData,
                          setSuccess,
                        })
                      }
                      onUploadImage={(fieldKey, files) =>
                        uploadMultipleImages({
                          docId: fieldKey,
                          files,
                          formId,
                          setLoading,
                          setResponseData,
                          setSuccess,
                        })
                      }
                      setLoading={setLoading}
                      setSuccess={setSuccess}
                    />
                  );
                }

                case "header":
                  return (
                    <Header
                      key={index}
                      text={module.text}
                      styles={localStyles}
                      isEditable={isFieldEditable}
                    />
                  );
                case "subheader":
                  return (
                    <SubHeader
                      key={index}
                      text={module.text}
                      styles={localStyles}
                      isEditable={isFieldEditable}
                    />
                  );
                case "site_information_viewer":
                  return (
                    <SiteInformationViewer
                      key={index}
                      siteId={siteId}
                      responses={responseData}
                      handleChange={handleFieldChange}
                      isEditable={isFieldEditable}
                    />
                  );
                case "image":
                  return (
                    <Box key={index} marginY={2} textAlign="center">
                      <img
                        src={module.url}
                        alt={module.alt_text}
                        style={{ maxWidth: "60%" }}
                      />
                    </Box>
                  );
                case "paragraph":
                  return <Paragraph key={index} content={module.content} />;
                case "short_answer":
                case "long_answer":
                  return (
                    <AnswerField
                      key={index}
                      module={module}
                      answer={answer}
                      isEditable={isFieldEditable}
                      fieldKey={key}
                      type={module.type}
                      onChange={handleFieldChange}
                    />
                  );

                case "inspection_item":
                  return (
                    <InspectionItemField
                      key={key}
                      module={module}
                      value={responseData[key]}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={key}
                      formId={formId}
                    />
                  );

                case "audit_checkbox_group":
                  return (
                    <AuditCheckboxGroupField
                      key={key}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={key}
                    />
                  );

                case "roofSiteInformationModule":
                  return (
                    <RoofPlanViewer
                      key={index}
                      siteId={siteId}
                      responses={responseData}
                      handleChange={handleFieldChange}
                      isEditable={isFieldEditable}
                    />
                  );
                case "checkbox":
                  return (
                    <CheckboxField
                      key={index}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={key}
                    />
                  );
                case "datetime":
                  return (
                    <DateTimeField
                      key={key}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      fieldKey={key}
                      onChange={handleFieldChange}
                    />
                  );
                case "checkbox_group":
                  return (
                    <CheckboxGroupField
                      key={index}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={key}
                      isCustom={false}
                    />
                  );
                case "custom_checkbox_group":
                  return (
                    <CheckboxGroupField
                      key={index}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={key}
                      isCustom={true}
                    />
                  );
                case "radio_button_group":
                  return (
                    <RadioButtonGroupField
                      key={index}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={key}
                    />
                  );

                case "custom_document_uploader": {
                  const fileOrUrl = responseData.documents?.[module.id];
                  return (
                    <CustomDocumentUploaderView
                      key={key}
                      module={module}
                      file={fileOrUrl}
                      isEditable={isFieldEditable}
                      onChange={(file) =>
                        handleFileChangeUpload(module.id, file, setResponseData)
                      }
                    />
                  );
                }

                case "hazard_register":
                  return (
                    <Box key={key} marginY={2}>
                      <Typography variant="h6">{module.label}</Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setHazardPopupOpen(true)}
                      >
                        Hazard & Risk Register Audit
                      </Button>
                    </Box>
                  );

                case "roofZoneHazardRegisterModule":
                  return (
                    <Box
                      key={key}
                      my={2}
                      display="flex"
                      justifyContent="center"
                    >
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setZoneName(module.zoneName || "Roof");
                          setZoneHazardPopupOpen(true);
                        }}
                        sx={{
                          borderColor: "#d00000",
                          color: "#d00000",
                          "&:hover": {
                            borderColor: "#a10000",
                            backgroundColor: "rgba(208,0,0,0.08)",
                          },
                        }}
                      >
                        {module.label || "View Zone Hazard Register"}
                      </Button>
                    </Box>
                  );

                case "video_embed": {
                  const url = (module.url || "").trim();
                  if (!url) return null;

                  const aspect =
                    Number(module.aspectRatio) > 0
                      ? Number(module.aspectRatio)
                      : 16 / 9;
                  const { kind, src, href } = buildEmbed(url, {
                    autoplay: !!module.autoplay,
                    muted: !!module.muted,
                    loop: !!module.loop,
                  });

                  return (
                    <Box key={key} my={2}>
                      {module.title && (
                        <Typography variant="h6" gutterBottom>
                          {module.title}
                        </Typography>
                      )}

                      {kind === "iframe" && (
                        <ResponsiveFrame aspect={aspect}>
                          <iframe
                            src={src}
                            title={module.title || "Embedded video"}
                            style={{ width: "100%", height: "100%", border: 0 }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            loading="lazy"
                          />
                        </ResponsiveFrame>
                      )}

                      {kind === "video" && (
                        <Box>
                          <video
                            src={src}
                            style={{
                              width: "100%",
                              borderRadius: 8,
                              display: "block",
                            }}
                            controls
                            playsInline
                            autoPlay={!!module.autoplay}
                            muted={!!module.muted}
                            loop={!!module.loop}
                            poster={module.poster || undefined}
                            preload="metadata"
                          />
                        </Box>
                      )}

                      {kind === "link" && (
                        <Alert severity="info" sx={{ my: 1 }}>
                          This link can't be embedded.{" "}
                          <Button
                            href={href}
                            target="_blank"
                            rel="noopener"
                            variant="outlined"
                            size="small"
                            sx={{ ml: 1 }}
                          >
                            Open video
                          </Button>
                        </Alert>
                      )}

                      {module.caption && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {module.caption}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                case "double_short_answer":
                  return (
                    <DoubleShortAnswerField
                      key={key}
                      module={module}
                      value={responseData[module.id] || {}}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={module.id || key}
                    />
                  );
                case "user_multi_select":
                  return (
                    <UserMultiSelectResponse
                      key={module.id || `module-${index}`}
                      module={module}
                      value={responseData[module.id]}
                      isEditable={isFieldEditable}
                      onChange={(newValue) =>
                        handleFieldChange(module.id, newValue)
                      }
                      companyId={companyId}
                      requestedTo={responseRequestedTo}
                    />
                  );

                case "service_provider_select":
                  return (
                    <ServiceProviderSelectResponse
                      key={module.id || `module-${index}`}
                      module={module}
                      value={responseData[module.id]}
                      isEditable={isFieldEditable}
                      onChange={(newValue) =>
                        handleFieldChange(module.id, newValue)
                      }
                      companyId={responseRequestedBy}
                      requestedTo={responseRequestedBy}
                    />
                  );

                case "time_range":
                  return (
                    <TimeRangeField
                      key={key}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      fieldKey={key}
                      onChange={handleTimeRangeChange}
                    />
                  );

                case "rectangle_link_button": {
                  const targetUrl = module.url;
                  return (
                    <RectangleLinkButton
                      key={module.id}
                      title={module.label}
                      url={targetUrl}
                      buttonColour={localStyles.buttonColor}
                      openInNewTab={module.openInNewTab}
                    />
                  );
                }

                case "image_upload": {
                  const uploadedImages = Array.isArray(answer) ? answer : [];
                  return (
                    <Box key={key} marginY={2}>
                      <Typography variant="h6">{module.label}</Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {uploadedImages.map((img, imgIndex) => (
                          <Box key={imgIndex} sx={{ position: "relative" }}>
                            <img
                              src={img}
                              alt={`Uploaded ${imgIndex + 1}`}
                              loading="lazy"
                              style={{ maxWidth: "150px" }}
                              crossOrigin="anonymous"
                            />
                            {isFieldEditable && (
                              <IconButton
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  color: "white",
                                }}
                                aria-label={`delete image ${imgIndex + 1}`}
                                onClick={() =>
                                  handleRemoveImageFromResponse(key, imgIndex)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                      {isFieldEditable && (
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          component="label"
                          sx={{ marginTop: 1 }}
                        >
                          Upload Images
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            multiple
                            onChange={(e) => {
                              if (e.target.files.length > 0) {
                                uploadMultipleImages({
                                  docId: key,
                                  files: e.target.files,
                                  formId,
                                  setLoading,
                                  setResponseData,
                                  setSuccess,
                                });
                              }
                            }}
                          />
                        </Button>
                      )}
                      {isFieldEditable && (
                        <Typography variant="caption" color="textSecondary">
                          {uploadedImages.length}/{module.imageLimit || "∞"}{" "}
                          images uploaded.
                        </Typography>
                      )}
                      <Divider sx={{ marginTop: 2, borderColor: "grey.300" }} />
                    </Box>
                  );
                }

                case "date_range":
                  return (
                    <DateRangeField
                      key={key}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      fieldKey={key}
                      onChange={handleDateRangeChange}
                    />
                  );

                case "photo_gallery": {
                  const galleryPhotos = answer || [];
                  return (
                    <Box key={index} marginY={2}>
                      <Typography variant="h6">{module.label}</Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {galleryPhotos.map((photo, photoIndex) => (
                          <Box key={photoIndex} sx={{ position: "relative" }}>
                            <img
                              src={photo}
                              alt={`Photo ${photoIndex + 1}`}
                              loading="lazy"
                              style={{ maxWidth: "150px" }}
                            />
                            {isFieldEditable && (
                              <IconButton
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  color: "white",
                                }}
                                aria-label={`delete photo ${photoIndex + 1}`}
                                onClick={() =>
                                  handleRemovePhoto(key, photoIndex)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                      {isFieldEditable && (
                        <Button
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          component="label"
                          sx={{ marginTop: 1 }}
                        >
                          Upload Photo
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                uploadPhoto({
                                  docId: key,
                                  file,
                                  formId,
                                  setLoading,
                                  setResponseData,
                                  setSuccess,
                                });
                              }
                            }}
                          />
                        </Button>
                      )}
                    </Box>
                  );
                }

                case "document_checker": {
                  const documentName =
                    module.documentName || "Unknown Document";
                  const formUrl = module.formUrl;
                  return (
                    <Box
                      key={key}
                      marginY={2}
                      p={2}
                      sx={{
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "95%",
                      }}
                    >
                      <Typography variant="h6">{documentName}</Typography>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        {documentStatuses[module.id] ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <>
                            {formUrl && (
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => {
                                  let newUrl = formUrl;
                                  const params = new URLSearchParams();
                                  params.append("response", responseId);
                                  if (siteId) {
                                    params.append("siteId", siteId);
                                  }
                                  newUrl +=
                                    (newUrl.includes("?") ? "&" : "?") +
                                    params.toString();
                                  window.open(newUrl, "_blank");
                                }}
                              >
                                Click here to complete
                              </Button>
                            )}
                            <CancelIcon color="error" />
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                }

                case "user_details":
                  return (
                    <UserDetails
                      key={index}
                      data={responseData.user_details || {}}
                      onChange={(field, value) =>
                        handleFieldChange(`user_details.${field}`, value)
                      }
                      isEditable={isFieldEditable}
                      styles={localStyles}
                      setUserId={setUserId}
                      setCompanyId={setCompanyId}
                      userId={userId}
                    />
                  );
                case "signature": {
                  const signature = signatures.find(
                    (sig) =>
                      sig.module_id === module.id || sig.module_id === key,
                  );
                  return (
                    <SignatureField
                      key={index}
                      module={module}
                      fieldKey={key}
                      signatureData={signature ? signature.signature_data : ""}
                      isEditable={isFieldEditable}
                      onSignatureChange={(dataURL) =>
                        handleSignatureChange(module.id || key, dataURL)
                      }
                      onRemove={() => handleRemoveSignature(module.id || key)}
                    />
                  );
                }
                case "table":
                  return (
                    <TableField
                      key={key}
                      module={module}
                      value={answer}
                      isEditable={isFieldEditable}
                      fieldKey={key}
                      onCellChange={handleTableCellChange}
                      onAddRow={handleAddTableRow}
                      onRemoveRow={handleRemoveTableRow}
                    />
                  );
                case "dropdown_select":
                  return (
                    <DropdownSelectField
                      key={module.id}
                      module={module}
                      value={
                        responseData[module.id] || (module.multiple ? [] : "")
                      }
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={module.id}
                    />
                  );

                case "email_dropdown":
                  return (
                    <DropdownSelectField
                      key={module.id}
                      module={module}
                      value={responseData[module.id] || []}
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={module.id}
                    />
                  );
                case "email":
                  return (
                    <EmailField
                      key={key}
                      module={module}
                      value={
                        responseData[module.id] || responseData.email || ""
                      }
                      isEditable={isFieldEditable}
                      onChange={handleFieldChange}
                      fieldKey={key}
                      confirmEmail={confirmEmail}
                      emailConfirmed={emailConfirmed}
                      emailError={emailError}
                    />
                  );
                case "dynamic_question_group":
                  return (
                    <DynamicQuestionGroupField
                      key={key}
                      module={module}
                      responses={responseData}
                    />
                  );
                default:
                  return null;
              }
            })}
        </div>

        {isHazardPopupOpen && (
          <HazardPopup
            originalHazards={hazards}
            onSave={handleLocalSave}
            onApply={handleApplyChanges}
            onClose={() => setHazardPopupOpen(false)}
            open={isHazardPopupOpen}
            currentUserId={userId}
          />
        )}
        {isZoneHazardPopupOpen && (
          <ZoneHazardPopup
            open={isZoneHazardPopupOpen}
            onClose={() => setZoneHazardPopupOpen(false)}
            siteId={siteId}
            zoneName={zoneName}
          />
        )}

        {requiredDocuments.length > 0 && (
          <Box marginY={2}>
            <Typography variant="h6" gutterBottom>
              Required Documents
            </Typography>
            {requiredDocuments.map((doc) => {
              const uploadedUrl = responseData.documents?.[doc.id];
              const filename =
                responseData.documents?.[doc.id]?.split("/").pop() || doc.name;
              return (
                <Box key={doc.id} marginY={1}>
                  <Typography variant="subtitle1">
                    {doc.name} {doc.required && "*"}
                  </Typography>
                  {uploadedUrl ? (
                    <Button
                      variant="text"
                      color="primary"
                      href={uploadedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<CloudUploadIcon />}
                      sx={{ marginRight: 2 }}
                    >
                      View {filename}
                    </Button>
                  ) : (
                    <Typography variant="body2">No file uploaded</Typography>
                  )}
                  {(isEditing ||
                    [3, 6, 7, 10, 11, 12].includes(Number(responseStatus))) && (
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      component="label"
                      sx={{ marginRight: 2 }}
                    >
                      {uploadedUrl ? "Change File" : "Upload File"}
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          handleFileChangeUpload({
                            docId: doc.id,
                            file,
                            formId,
                            setLoading,
                            setResponseData,
                            setSuccess,
                          });
                        }}
                      />
                    </Button>
                  )}
                  {!doc.required && (
                    <Typography variant="caption" color="textSecondary">
                      (Optional)
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* fixed footer with Save/Submit buttons */}
        {(isEditing || isSaveButtonVisible) && (
          <Box
            component="footer"
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              backgroundColor: "background.paper",
              boxShadow: 3,
              px: 2,
              py: 1,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            {/* Live audit banner — null-guarded, only renders for Audit forms */}
            {auditBanner && (
              <Box
                sx={{
                  mb: 1,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: auditBanner.color,
                  color: "#fff",
                  textAlign: "center",
                  fontWeight: 600,
                  maxWidth: 600,
                  mx: "auto",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                }}
              >
                {auditBanner.percentLabel && (
                  <Typography
                    component="span"
                    sx={{
                      display: "block",
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      lineHeight: 1.1,
                      mb: 0.25,
                    }}
                  >
                    {auditBanner.percentLabel}
                  </Typography>
                )}
                <Typography
                  component="span"
                  sx={{ fontSize: "0.85rem", fontWeight: 700 }}
                >
                  {auditBanner.mandatoryFailBanner
                    ? "FAIL — Critical Risk Identified"
                    : `This Audit will submit as ${auditBanner.text}${
                        auditBanner.showCounts ? ` — ${countsText}` : ""
                      }`}
                </Typography>
              </Box>
            )}

            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              sx={{ width: "100%", maxWidth: 600, mx: "auto" }}
            >
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSaveChanges}
                disabled={loading}
                sx={{
                  backgroundColor: localStyles.buttonColor,
                  "&:hover": {
                    backgroundColor: localStyles.buttonColor,
                  },
                }}
              >
                {loading ? <CircularProgress size={20} /> : "Save Changes"}
              </Button>
              {(responseStatus === 2 ||
                responseStatus === 1 ||
                responseStatus === 7) && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    backgroundColor: localStyles.buttonColor,
                    "&:hover": {
                      backgroundColor: localStyles.buttonColor,
                    },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : "Submit"}
                </Button>
              )}
            </Stack>
          </Box>
        )}

        <Snackbar
          open={applySuccess}
          autoHideDuration={6000}
          onClose={() => setApplySuccess(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setApplySuccess(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Changes applied successfully!
          </Alert>
        </Snackbar>

        <Snackbar
          open={Boolean(applyError)}
          autoHideDuration={6000}
          onClose={() => setApplyError("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setApplyError("")}
            severity="error"
            sx={{ width: "100%" }}
          >
            {applyError}
          </Alert>
        </Snackbar>

        <Snackbar
          open={success}
          message="Operation completed successfully!"
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
          action={
            <Button
              color="secondary"
              size="small"
              onClick={() => {
                if (uploadedUrl) {
                  window.open(uploadedUrl, "_blank");
                }
              }}
              disabled={!uploadedUrl}
            >
              View Response
            </Button>
          }
        />
      </Container>
    </LocalizationProvider>
  );
}

export default ViewResponsePage;